import * as Sentry from '@sentry/remix';

import type { PostMetaInformation } from '~/interfaces/post';
import getCookieValue from '~/utils/getCookieValue';
import { safeJsonParse } from '~/utils/safeJsonParse';

interface FetchPostMetaInformationParams {
  slug: string;
  isDraft: boolean;
  requestUrl: string;
  cookies: string;
  commentsEnabled: boolean;
  hidePremiumPost: boolean;
  paywallShown?: boolean;
  meteredUsageShown?: boolean;
  forcePremium: boolean;
  enforcePaywallBreaks?: boolean;
  containsSubscriberBreak: boolean;
  meteredViewedCount?: number | null;
  previewTierIds: string[];
  previewSubscriberId?: string;
  dynamicContentOverrides?: string[];
  previewJwtToken?: string | null;
}

/**
 * Fetches post meta information from the API or returns default values
 *
 * @param params - Configuration for fetching post meta information
 * @returns PostMetaInformation object with visibility and paywall settings
 */
export async function fetchPostMetaInformation(params: FetchPostMetaInformationParams): Promise<PostMetaInformation> {
  const {
    slug,
    isDraft,
    requestUrl,
    cookies,
    commentsEnabled,
    hidePremiumPost,
    paywallShown,
    meteredUsageShown,
    forcePremium,
    enforcePaywallBreaks,
    containsSubscriberBreak,
    meteredViewedCount,
    previewTierIds,
    previewSubscriberId,
    dynamicContentOverrides,
    previewJwtToken,
  } = params;

  // Default to server data
  const defaultMetaInformation: PostMetaInformation = {
    comments_enabled: commentsEnabled,
    hide_premium_post: hidePremiumPost,
    paywall_shown: paywallShown,
    metered_usage_shown: meteredUsageShown,
    metered_paywall_limit_exceeded: false,
    metered_viewed_count: meteredViewedCount,
    force_premium: forcePremium,
    enforce_paywall_breaks: enforcePaywallBreaks,
    contains_subscriber_break: containsSubscriberBreak,
    post_tiptap_state: null,
  };

  try {
    const url = new URL(requestUrl);
    const giftContent = url.searchParams.get('gift_content');
    const jwtToken = url.searchParams.get('jwt_token') || getCookieValue('token', cookies);

    const postMetaInformationResponse = await fetch('/post_meta_information', {
      method: 'POST',
      headers: { 'Sec-Fetch-Dest': 'empty' },
      body: JSON.stringify({
        slug,
        draft: isDraft,
        jwt_token: jwtToken,
        gift_slug: giftContent,
        preview_tier_ids: previewTierIds,
        preview_subscriber_id: previewSubscriberId,
        dynamic_content_overrides: dynamicContentOverrides,
        preview_jwt_token: previewJwtToken,
      }),
    });

    const result = await safeJsonParse<PostMetaInformation>(postMetaInformationResponse);
    return { ...result, _fetched: true };
  } catch (error) {
    Sentry.captureException(error);
    return { ...defaultMetaInformation, _fetched: true };
  }
}
